// client/src/utils/phone.js — Maxula — v1.448.0
// ============================================================================
// TELEPHONE : VALIDATION DE SAISIE ET AFFICHAGE
//
// Reprend la meme regle que server/utils/phoneMA.js. Les deux doivent rester
// identiques : une regle plus stricte devant que derriere ne protege de rien,
// elle empeche seulement de s'inscrire ; une regle plus laxiste laisse le
// serveur refuser apres coup, apres que l'utilisateur a tout ressaisi.
//
// STOCKAGE (decide par le serveur)
//   Maroc    -> 0XXXXXXXXX
//   Etranger -> +CCXXXXXXXX (E.164)
//
// Hors du Maroc, un numero n'a aucun sens sans son indicatif : le 0 initial
// francais, par exemple, ne se compose pas depuis le Maroc.
// ============================================================================

// Indicatifs telephoniques internationaux (UIT-T E.164).
// Liste fermee et non « + suivi de chiffres » : sans elle, +999 passerait.
export const INDICATIFS = [
  '1', '7', '20', '27', '30', '31', '32', '33', '34', '36', '39', '40', '41', '43', '44', '45',
  '46', '47', '48', '49', '51', '52', '53', '54', '55', '56', '57', '58', '60', '61', '62', '63',
  '64', '65', '66', '81', '82', '84', '86', '90', '91', '92', '93', '94', '95', '98',
  '211', '212', '213', '216', '218', '220', '221', '222', '223', '224', '225', '226', '227',
  '228', '229', '230', '231', '232', '233', '234', '235', '236', '237', '238', '239', '240',
  '241', '242', '243', '244', '245', '248', '249', '250', '251', '252', '253', '254', '255',
  '256', '257', '258', '260', '261', '262', '263', '264', '265', '266', '267', '268', '269',
  '290', '291', '297', '298', '299', '350', '351', '352', '353', '354', '355', '356', '357',
  '358', '359', '370', '371', '372', '373', '374', '375', '376', '377', '378', '380', '381',
  '382', '383', '385', '386', '387', '389', '420', '421', '423', '500', '501', '502', '503',
  '504', '505', '506', '507', '508', '509', '590', '591', '592', '593', '595', '597', '598',
  '599', '670', '672', '673', '674', '675', '676', '677', '678', '679', '680', '681', '682',
  '683', '685', '686', '687', '688', '689', '690', '691', '692', '850', '852', '853', '855',
  '856', '870', '880', '886', '960', '961', '962', '963', '964', '965', '966', '967', '968',
  '970', '971', '972', '973', '974', '975', '976', '977', '992', '993', '994', '995', '996', '998',
];
const INDICATIFS_TRIES = INDICATIFS.slice().sort((a, b) => b.length - a.length);

/** Le numero saisi est-il acceptable (Maroc ou etranger) ? */
export function telephoneValide(valeur) {
  const brut = String(valeur || '').trim();
  if (!brut) return false;

  const chiffres = brut.replace(/[^\d]/g, '');
  const annonceMaroc = /^(\+|00)?212/.test(brut.replace(/[\s.\-_/]/g, '')) || chiffres.startsWith('212');

  // --- Etranger : un indicatif doit etre annonce explicitement ---
  if (!annonceMaroc && /^(\+|00)/.test(brut)) {
    let n = chiffres;
    if (n.startsWith('00')) n = n.slice(2);
    const ind = INDICATIFS_TRIES.find((i) => n.startsWith(i));
    if (!ind) return false;
    const reste = n.slice(ind.length);
    return reste.length >= 6 && n.length <= 15;
  }

  // --- Maroc ---
  let n = chiffres;
  if (n.startsWith('00212')) n = n.slice(2);
  if (n.startsWith('212')) n = `0${n.slice(3)}`;
  if (n.length === 9 && !n.startsWith('0')) n = `0${n}`;
  if (n.length !== 10 || !n.startsWith('0')) return false;
  if (!['05', '06', '07'].includes(n.slice(0, 2))) return false;
  // Numero de remplissage (0600000000) : refuse, sinon le champ obligatoire
  // ne sert a rien.
  if (/^(\d)\1{7}$/.test(n.slice(2))) return false;
  return true;
}

/**
 * Affichage lisible, quel que soit le pays.
 * Maroc    : 06 12 34 56 78
 * Etranger : +33 6 12 34 56 78
 */
export function formaterTelephone(valeur) {
  const brut = String(valeur || '').trim();
  if (!brut) return '';

  if (brut.startsWith('+')) {
    const n = brut.replace(/[^\d]/g, '');
    const ind = INDICATIFS_TRIES.find((i) => n.startsWith(i));
    if (!ind) return brut;
    const reste = n.slice(ind.length);
    // Groupage par deux EN PARTANT DE LA DROITE : de gauche a droite, un
    // numero de longueur impaire finit par un chiffre isole, qui se lit mal
    // et donne l'impression d'une troncature.
    const groupes = [];
    for (let i = reste.length; i > 0; i -= 2) groupes.unshift(reste.slice(Math.max(0, i - 2), i));
    return `+${ind} ${groupes.join(' ')}`;
  }

  const n = brut.replace(/[^\d]/g, '');
  if (n.length !== 10) return brut;
  return `${n.slice(0, 2)} ${n.slice(2, 4)} ${n.slice(4, 6)} ${n.slice(6, 8)} ${n.slice(8, 10)}`;
}

/** Lien composable : conserve le '+' international, indispensable pour appeler. */
export function lienTel(valeur) {
  const brut = String(valeur || '').trim();
  if (!brut) return '';
  return brut.startsWith('+') ? `+${brut.replace(/[^\d]/g, '')}` : brut.replace(/[^\d]/g, '');
}

/** Numero etranger ? Sert a afficher un repere visuel dans les listes. */
export function estEtranger(valeur) {
  return String(valeur || '').trim().startsWith('+');
}
